import React from 'react'
import { observer } from 'mobx-react'
import cx from 'classnames'
import styles from './FilterControls.sass'
import {
  Dropdown,
  DropdownItem,
  DropdownItemDescription,
  DropdownItemTitle,
  DropdownScroll,
  DropdownTitle,
} from 'components/Dropdown'
import Icon from 'components/Icon'
import {
  FiltersViewModel,
  IFilterOption,
  IFilterViewModel,
} from 'infra/FiltersViewModel'
import Button from 'components/Button'
import Chip from 'components/Chip'
import { NumberRangeFilter } from './NumberRange/NumberRangeFilter'
import { DateRangeFilterView } from './DateRange'
import { NumberRangeFilterView } from './NumberRange'
import { SelectFilterView } from './Select'
import { SelectFilter } from './Select/SelectFilter'
import { DateRangeFilter } from './DateRange/DateRangeFilter'
import { InfiniteSelectFilter } from './InfiniteSelect/InfiniteSelectFilter'
import { InfiniteSelectFilterView } from './InfiniteSelect'
import { FlagFilter } from './Boolean/FlagFilter'
import { SingleSelectFilter } from './SingleSelect/SingleSelectFilter'
import { SingleSelectFilterView } from './SingleSelect'

// TODO: Remove `job-` prefix from data-test attrs, or allow passing it in.

interface FilterControlsProps<TFilters extends Record<string, unknown>> {
  vm: FiltersViewModel<TFilters>
  active: boolean
  dataTestSubject?: string
}

export default observer(function FilterControls<
  TFilters extends Record<string, unknown>,
>({ vm, active, dataTestSubject = 'job' }: FilterControlsProps<TFilters>) {
  return (
    <div
      className={cx(
        styles.filterControls,
        active && styles.filterControlsActive,
      )}
    >
      <div className={cx(styles.selectedFiltersRow)}>
        <AppliedFilterList vm={vm} />
        <Dropdown
          autoDismiss
          position="bottom-start"
          button={
            <Button
              type="text"
              data-test={`${dataTestSubject}-filter-add-filter-button`}
            >
              + Add Filter
            </Button>
          }
        >
          <DropdownScroll>
            <AvailableFiltersView vm={vm} dataTestSubject={dataTestSubject} />
          </DropdownScroll>
        </Dropdown>
      </div>
      <Button
        type="text"
        data-test={`${dataTestSubject}-filter-clear-all-button`}
        onClick={vm.clearExplicitFilters}
      >
        Clear All
      </Button>
    </div>
  )
})

interface AppliedFilterListProps<TFilters extends Record<string, unknown>> {
  vm: FiltersViewModel<TFilters>
}

const AppliedFilterList = observer(function AppliedFilterList<
  TFilters extends Record<string, unknown>,
>({ vm }: AppliedFilterListProps<TFilters>) {
  return (
    <div className={cx(styles.selectedFilterList)}>
      {vm.explicitFilterVms.map((f) => (
        <AppliedFilterChip key={f.key} filter={f} vm={vm} />
      ))}
    </div>
  )
})

interface AppliedFilterChipProps<TFilters extends Record<string, unknown>> {
  filter: IFilterViewModel<string, unknown>
  vm: FiltersViewModel<TFilters>
}

const AppliedFilterChip = observer(function AppliedFilterChip<
  TFilters extends Record<string, unknown>,
>({ filter, vm }: AppliedFilterChipProps<TFilters>) {
  return (
    <Dropdown
      isOpen={filter.editing}
      contentOverButton
      allowOverflow
      onRequestClose={filter.stopEditing}
      position="bottom-start"
      button={
        <div className={styles.selectedFilter}>
          <Chip
            variant="filter"
            onClick={filter.startEditing}
            onRemoveClick={() => vm.clearFilter(filter.key)}
          >
            {filter.toString()}
          </Chip>
        </div>
      }
    >
      {filter instanceof DateRangeFilter ? (
        <DateRangeFilterView filter={filter} />
      ) : filter instanceof InfiniteSelectFilter ? (
        <InfiniteSelectFilterView filter={filter} />
      ) : filter instanceof SelectFilter ? (
        <SelectFilterView filter={filter} />
      ) : filter instanceof SingleSelectFilter ? (
        <SingleSelectFilterView filter={filter} />
      ) : filter instanceof NumberRangeFilter ? (
        <NumberRangeFilterView
          filter={filter}
          unitName="price"
          currencyCode="USD"
        />
      ) : filter instanceof FlagFilter ? (
        <></>
      ) : (
        /* This will never be reached unless a dev adds a new filter without adding a UI for it. Would be a developer error */
        <>No filter found</>
      )}
    </Dropdown>
  )
})

interface FilterViewHeaderProps {
  title: string
  dataTest?: string
  onClear: () => void
  onSelectAll?: () => void
}

export const FilterViewHeader = observer(function FilterHeader({
  title,
  dataTest,
  onClear,
  onSelectAll,
}: FilterViewHeaderProps) {
  return (
    <div className={cx(styles.filterTitleRow)}>
      <DropdownTitle>{title}</DropdownTitle>
      {onSelectAll && (
        <div className={cx(styles.action)}>
          <Button type="text" onClick={onSelectAll}>
            Select all
          </Button>
        </div>
      )}
      <div className={cx(styles.action)}>
        <Button type="text" data-test={dataTest} onClick={onClear}>
          Clear
        </Button>
      </div>
    </div>
  )
})

interface FilterViewItemContainerProps {
  children: React.ReactNode
}

export const FilterViewItemContainer = function FilterViewItemContainer({
  children,
}: FilterViewItemContainerProps) {
  return <div className={styles.filterItemContainer}>{children}</div>
}

interface FilterViewItemProps {
  title: string
  children: React.ReactNode
}

export const FilterViewItem = observer(function FilterViewItem({
  title,
  children,
}: FilterViewItemProps) {
  return (
    <div className={cx(styles.filterItem)}>
      <div className={cx(styles.filterItemText)}>{title}</div>
      {children}
    </div>
  )
})

export function FilterViewDivider() {
  return <div className={styles.divider} />
}

interface MainFiltersProps<TFilters extends Record<string, unknown>> {
  vm: FiltersViewModel<TFilters>
  dataTestSubject?: string
}

const AvailableFiltersView = observer(function AvailableFiltersView<
  TFilters extends Record<string, unknown>,
>({ vm, dataTestSubject }: MainFiltersProps<TFilters>) {
  return (
    <>
      <DropdownTitle>Add filter</DropdownTitle>
      {vm.availableFilters
        .filter((f) => f.isVisible())
        .map((f) => (
          <AvailableFilterItem
            key={f.key}
            filter={f}
            vm={vm}
            dataTestSubject={dataTestSubject}
          />
        ))}
    </>
  )
})

interface MainFilterItemProps<TFilters extends Record<string, unknown>> {
  filter: IFilterOption<string, unknown>
  vm: FiltersViewModel<TFilters>
  dataTestSubject?: string
}

const AvailableFilterItem = observer(function AvailableFilterItem<
  TFilters extends Record<string, unknown>,
>({ filter, vm, dataTestSubject }: MainFilterItemProps<TFilters>) {
  return (
    <DropdownItem
      icon={<Icon name={filter.icon!} color="var(--grey600)" />}
      data-test={`${dataTestSubject}-filter-type-selector-` + filter.title}
      onClick={() =>
        filter.current ? vm.editFilter(filter.key) : vm.addFilter(filter.key)
      }
    >
      <DropdownItemTitle>{filter.title}</DropdownItemTitle>
      {filter.current && (
        <DropdownItemDescription>
          {filter.current.toString()}
        </DropdownItemDescription>
      )}
    </DropdownItem>
  )
})

interface FilterSearchBarProps {
  onChange: (val: string) => void
}

export function FilterSearchBar({ onChange }: FilterSearchBarProps) {
  const change = React.useCallback(
    (e: any) => {
      onChange(e.target.value)
    },
    [onChange],
  )

  return (
    <div className={cx(styles.subFilterContent)}>
      <div className={styles.filterSearch}>
        <input
          type="text"
          placeholder="Search"
          className={styles.searchInput}
          onChange={change}
        />
        <Icon name="search" />
      </div>
    </div>
  )
}
